import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { Thread, ThreadStatus, ThreadUser, WithAvatar, WithName } from '@schooly/api';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import { EmptyListSvg, SimpleCard, SimpleCardProps, TruncatedText } from '@schooly/style';
import { getUserFullName } from '@schooly/utils/get-user-full-name';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { MessageContent } from '../ChannelMessages/Message';
import { UpdateThreadComponent } from '../CloseThreads';
import { useGetSortedThreadParticipants } from '../ThreadList';
import { ChannelTextField } from './ChannelInput';

export const NoChannelSelectedStub = () => {
  const { formatMessage } = useIntl();

  return (
    <Stack height="100%" justifyContent="center" alignItems="center">
      <Stack gap={3} alignItems="center">
        <Typography variant="h3" color="common.grey2">
          {formatMessage({ id: 'messages-ThreadNoChannelSelected' })}
        </Typography>
        <Typography variant="h3" color="common.grey">
          {formatMessage({ id: 'messages-ThreadSelectChatToView' })}
        </Typography>
      </Stack>
    </Stack>
  );
};

export const NoMessagesStub = () => {
  const { formatMessage } = useIntl();

  return (
    <Stack
      alignItems="center"
      gap={3}
      sx={{
        ' svg': {
          width: 180,
          height: 180,
        },
      }}
    >
      <EmptyListSvg />
      <Typography textAlign="center" variant="h3" color="common.grey2" maxWidth={280}>
        {formatMessage({ id: 'messages-NoMessages' })}
      </Typography>
    </Stack>
  );
};

export const ChannelLoading = () => {
  return (
    <Stack height="100%" justifyContent="space-between" px={2} pt={2}>
      <Stack gap={8}>
        <Stack direction="row" gap={2} width="100%" justifyContent="space-between">
          <Box width="100%">
            <ChannelUserCard isLoading />
          </Box>
          <Box width="100%">
            <ChannelUserCard isLoading />
          </Box>
        </Stack>
        <Stack gap={0.5}>
          <MessageContent
            isLoading
            isMyMessage={false}
            minWidth="370px"
            user={{
              given_name: '',
              last_name: '',
            }}
          />
          <MessageContent minWidth="118px" isLoading isMyMessage={false} />
        </Stack>
      </Stack>
      <ChannelTextField isLoading />
    </Stack>
  );
};

type ChannelUserCardProps = {
  user?: WithName & WithAvatar;
  status?: string;
  isLoading?: boolean;
  link?: string;
} & SimpleCardProps;

export const ChannelUserCard: FC<ChannelUserCardProps> = ({
  user,
  status,
  isLoading,
  link,
  ...props
}) => {
  return (
    <SimpleCard
      loadingState={
        isLoading
          ? {
              avatarSize: 30,
              // biome-ignore lint/correctness/useJsxKeyInIterable: Legacy code
              skeletons: [<Skeleton variant="text" width="44%" height={18} />],
            }
          : undefined
      }
      actions={
        <Stack>
          <Typography color="common.grey3">{status}</Typography>
        </Stack>
      }
      {...props}
      sx={{
        borderBottom: 'none',
        borderRadius: (theme) => theme.spacing(1),
        ...props.sx,
      }}
    >
      <Stack direction="row" gap={1} flex={1}>
        <Typography
          variant="h3"
          color="common.grey2"
          {...(link && { component: Link, to: { pathname: link } })}
          sx={{ '&:hover': { textDecoration: link ? 'underline' : undefined } }}
        >
          {user ? getUserFullName(user) : ''}
        </Typography>
      </Stack>
    </SimpleCard>
  );
};

type ChannelUserListProps = {
  users: ThreadUser[];
  getLink: (user: ThreadUser) => string;
  title?: string;
};

const ChannelUserList: FC<ChannelUserListProps> = ({ users, getLink, title }) => (
  <Stack height="100%" width="100%">
    {title && (
      <Typography pb={1} variant="h4" color="common.main3">
        {title}
      </Typography>
    )}
    <Stack borderRadius={1} overflow="hidden">
      {users.map((user, i, arr) => {
        const isLast = i === arr.length - 1;
        return (
          <ChannelUserCard
            avatar={<AvatarAuth user={user} />}
            key={user.user_id}
            user={user}
            sx={{
              '&:hover': {
                '.MuiCardActionArea-focusHighlight': {
                  background: 'transparent',
                },
                '.MuiCardActionArea-root': {
                  background: (theme) => theme.palette.common.lightBg,
                },
              },
              borderRadius: arr.length <= 1 ? 1 : 0,
              borderBottom: (theme) =>
                isLast ? 'none' : `1px solid ${theme.palette.common.light2}`,
            }}
            link={getLink(user)}
          />
        );
      })}
    </Stack>
  </Stack>
);

type ChannelPreviewProps = {
  thread: Thread;
  getRoutePath: (
    type: 'student' | 'parent',
    params: { id: string; relation_id?: string },
  ) => string;
};

export const ChannelPreview: FC<ChannelPreviewProps> = ({ thread, getRoutePath }) => {
  const { formatMessage } = useIntl();
  return (
    <Stack mx={-2} pb={4}>
      <Stack direction="row" gap={2} mb={2} mt={2.75}>
        <ChannelUserList
          users={thread.children}
          title={formatMessage({ id: 'userType-student-plural' })}
          getLink={(user) =>
            getRoutePath('student', {
              id: user.user_id,
              relation_id: user.relation_id ?? undefined,
            })
          }
        />
        <ChannelUserList
          users={thread.parents}
          title={formatMessage({ id: 'userType-parentsGuardians' })}
          getLink={(user) =>
            getRoutePath('parent', {
              id: user.user_id,
              relation_id: user.relation_id ?? undefined,
            })
          }
        />
      </Stack>
      <Typography variant="body1" color="common.grey2">
        {formatMessage({ id: 'messages-ThreadChannelDescription' })}
      </Typography>
    </Stack>
  );
};

type ChannelHeaderProps = {
  thread: Thread;
  onThreadStatusUpdate?: (thread: Thread, status: ThreadStatus) => void;
  isThreadClosed: boolean;
};

export const ChannelHeader: FC<ChannelHeaderProps> = ({
  thread,
  onThreadStatusUpdate,
  isThreadClosed,
}) => {
  const { formatMessage } = useIntl();

  const [sortedChildren, sortedParents] = useGetSortedThreadParticipants(thread);

  const [sortedParentNames, sortedChildNames] = useMemo(
    () => [
      sortedParents.map((p) => getUserFullName(p)),
      sortedChildren.map((c) => getUserFullName(c)),
    ],
    [sortedParents, sortedChildren],
  );

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: 'background.paper',
        borderBottom: `1px solid ${theme.palette.common.light2}`,
      })}
    >
      <Stack direction="row" gap={1} px={2.5} py={1}>
        <AvatarAuth sx={{ width: 36, height: 36 }} user={sortedChildren[0]} />
        <Stack>
          <TruncatedText
            items={sortedChildNames}
            typographyProps={{
              variant: 'h3',
              color: 'common.grey2',
            }}
          />
          <TruncatedText
            items={sortedParentNames.map((n, i) =>
              i === 0 ? `${formatMessage({ id: 'userType-parent-plural' })}: ${n}` : n,
            )}
            typographyProps={{
              color: 'common.grey',
            }}
            tooltipProps={{ title: sortedParentNames.join(', ') }}
          />
        </Stack>

        {onThreadStatusUpdate && (
          <Stack flex={1} alignItems="flex-end" justifyContent="center">
            <UpdateThreadComponent
              onThreadStatusUpdate={() =>
                onThreadStatusUpdate(
                  thread,
                  isThreadClosed ? ThreadStatus.ACTIVE : ThreadStatus.ARCHIVED,
                )
              }
              label={formatMessage(
                { id: 'messages-ThreadForFamilyAction' },
                {
                  action: formatMessage({
                    id: isThreadClosed ? 'action-Open' : 'action-Close',
                  }),
                },
              )}
              isThreadClosed={isThreadClosed}
            />
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
